import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfig } from 'app/config/service.config';
import {
    BehaviorSubject,
    Observable,
    ReplaySubject,
    of,
    throwError,
} from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { LogService } from '../logging/log.service';
import {
    Admins,
    Aging,
    Approval,
    BranchObj,
    CoinsHistory,
    Conversion,
    ConversionPagination,
    CountryCode,
    DeleteDiscount,
    DeleteVariant,
    Discount,
    DiscountPagination,
    DiscountUpdate,
    Earner,
    ImageDetails,
    LoyaltyConfig,
    Members,
    MembershipInfo,
    MicroDealer,
    MicrodealerDetails,
    Pagination,
    Product,
    ProductCategoryDetails,
    ProductType,
    ProductsPagination,
    ReferralProducts,
    ReferralService,
    ReferralTree,
    ReferralUsers,
    Reward,
    UserDoc,
    productDiscount,
} from '../product/product.types';
import { CategoryManagement } from '../product/product-management.types';
import { environment } from 'environments/environment';
import { BrandService } from '../brand/brand.service';

@Injectable({
    providedIn: 'root',
})
export class BillService {
    private _bills: ReplaySubject<Product[]> = new ReplaySubject<Product[]>(1);
    private _bill: ReplaySubject<Product> = new ReplaySubject<Product>(1);

    private _categories: ReplaySubject<ProductCategoryDetails[]> = new ReplaySubject<ProductCategoryDetails[]>(1);
    private _category: ReplaySubject<ProductCategoryDetails> = new ReplaySubject<ProductCategoryDetails>(1);

    private _subCategories = new ReplaySubject<ProductCategoryDetails[]>(1);
    private _subCategory = new ReplaySubject<ProductCategoryDetails>(1);

    private _countries: ReplaySubject<CountryCode[]> = new ReplaySubject<CountryCode[]>(1);
    private _country: ReplaySubject<CountryCode> = new ReplaySubject<CountryCode>(1);

    private _variantType: ReplaySubject<ProductType[]> = new ReplaySubject<ProductType[]>(1);

    private _billsPagination: BehaviorSubject<ProductsPagination | null> = new BehaviorSubject(null);

    private _discounts: ReplaySubject<Discount[]> = new ReplaySubject<Discount[]>(1);
    private _discount: ReplaySubject<Discount> = new ReplaySubject<Discount>(1);
    private _discountPagination: BehaviorSubject<DiscountPagination | null> = new BehaviorSubject(null);

    private _delete: ReplaySubject<DeleteVariant> = new ReplaySubject<DeleteVariant>(1);

    private _rewards: ReplaySubject<Reward[]> = new ReplaySubject<Reward[]>(1);

    private _conversionPagination: BehaviorSubject<ConversionPagination | null> = new BehaviorSubject(null);
    private _conversion: ReplaySubject<Conversion[]> = new ReplaySubject<Conversion[]>(1);

    private _treeProducts: ReplaySubject<BranchObj[]> = new ReplaySubject<BranchObj[]>(1);

    private _referralUsers: ReplaySubject<ReferralUsers[]> = new ReplaySubject<ReferralUsers[]>(1);
    private _referralUsersPagination: BehaviorSubject<Pagination | null> = new BehaviorSubject(null);


    private _referralProducts: ReplaySubject<ReferralProducts[]> = new ReplaySubject<ReferralProducts[]>(1);
    private _referralProductsPagination: BehaviorSubject<ProductsPagination | null> = new BehaviorSubject(null);

    private _coinsHistory: ReplaySubject<CoinsHistory[]> = new ReplaySubject<CoinsHistory[]>(1);
    private _coinsHistoryPagination: BehaviorSubject<Pagination | null> = new BehaviorSubject(null);


    private _topEarner: ReplaySubject<Earner[]> = new ReplaySubject<Earner[]>(1);

    private _agingData: ReplaySubject<Aging[]> = new ReplaySubject<Aging[]>(1);
    private _agingDataPagination: BehaviorSubject<Pagination | null> = new BehaviorSubject(null);


    private _membersList: ReplaySubject<Members[]> = new ReplaySubject<Members[]>(1);
    private _membersListPagination: BehaviorSubject<Pagination | null> = new BehaviorSubject(null);

    private _referralTree: ReplaySubject<ReferralTree> = new ReplaySubject<ReferralTree>(1);

    private _userDoc: ReplaySubject<UserDoc[]> = new ReplaySubject<UserDoc[]>(1);

    private _loyaltyService: ReplaySubject<ReferralService[]> = new ReplaySubject<ReferralService[]>(1);
    private _microService: ReplaySubject<ReferralService[]> = new ReplaySubject<ReferralService[]>(1);
    private _referralService: ReplaySubject<ReferralService[]> = new ReplaySubject<ReferralService[]>(1);

    private _microDealer: ReplaySubject<MicrodealerDetails[]> = new ReplaySubject<MicrodealerDetails[]>(1);
    private _loyaltyConfig: ReplaySubject<LoyaltyConfig[]> = new ReplaySubject<LoyaltyConfig[]>(1);
    private _loyaltyConfigPagination: BehaviorSubject<Pagination | null> = new BehaviorSubject(null);

    private _membershipInfo: ReplaySubject<MembershipInfo> = new ReplaySubject<MembershipInfo>(1);

    private _count: ReplaySubject<any> = new ReplaySubject<any>(1);
    private _approvalData: ReplaySubject<Approval> = new ReplaySubject<Approval>(1);

    private _admins: ReplaySubject<Admins[]> = new ReplaySubject<Admins[]>(1);

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _apiServer: AppConfig,
        private _logging: LogService,
        private _authService: AuthService,
        private _brandService: BrandService
    ) { }

    private getServiceEndpoint(): string {
        return this._brandService.getApiEndpoint(this._apiServer);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /** Getter & Setter for bill */
    set bill(value: Product) {
        this._bill.next(value);
    }
    get bill$(): Observable<Product> {
        return this._bill.asObservable();
    }

    /** Getter & Setter for count */
    set count(value: any) {
        this._count.next(value);
    }
    get count$(): Observable<any> {
        return this._count.asObservable();
    }

    /** Getter & Setter for bills */
    set bills(value: Product[]) {
        this._bills.next(value);
    }
    get bills$(): Observable<Product[]> {
        return this._bills.asObservable();
    }

    /** Setter and Getter for productsManagement */
    set categories(value: ProductCategoryDetails[]) {
        this._categories.next(value);
    }
    get categories$(): Observable<ProductCategoryDetails[]> {
        return this._categories.asObservable();
    }

    /** Setter and Getter for productsManagement */
    set subCategories(value: ProductCategoryDetails[]) {
        this._subCategories.next(value);
    }
    get subCategories$(): Observable<ProductCategoryDetails[]> {
        return this._subCategories.asObservable();
    }

    /** Setter and Getter for productsManagement */
    set subCategory(value: ProductCategoryDetails) {
        this._subCategory.next(value);
    }
    get subCategory$(): Observable<ProductCategoryDetails> {
        return this._subCategory.asObservable();
    }

    /** Getter for bills pagination */
    get billsPagination$(): Observable<ProductsPagination> {
        return this._billsPagination.asObservable();
    }

    /** Setter and Getter for countries */
    set countries(value: CountryCode[]) {
        this._countries.next(value);
    }
    get countries$(): Observable<CountryCode[]> {
        return this._countries.asObservable();
    }

    /** Setter and Getter for varinatType */
    set variantTypes(value: ProductType[]) {
        this._variantType.next(value);
    }
    get variantTypes$(): Observable<ProductType[]> {
        return this._variantType.asObservable();
    }

    /** Setter and Getter for Discount */
    set discounts(value: Discount[]) {
        this._discounts.next(value);
    }
    get discounts$(): Observable<Discount[]> {
        return this._discounts.asObservable();
    }

    /** Setter and Getter for Discount */
    set discount(value: Discount) {
        this._discount.next(value);
    }
    get discount$(): Observable<Discount> {
        return this._discount.asObservable();
    }

    /** Setter and Getter for Delete Variant */
    set delete(value: DeleteVariant) {
        // Store the value
        this._delete.next(value);
    }

    get delete$(): Observable<DeleteVariant> {
        return this._delete.asObservable();
    }

    /** Setter and Getter for Rewards */
    set rewards(value: Reward[]) {
        // Store the value
        this._rewards.next(value);
    }

    get rewards$(): Observable<Reward[]> {
        return this._rewards.asObservable();
    }

    /** Setter and Getter for Conversions */
    set conversion(value: Conversion[]) {
        // Store the value
        this._conversion.next(value);
    }

    get conversion$(): Observable<Conversion[]> {
        return this._conversion.asObservable();
    }

    /** Setter and Getter for Conversions Paginations */
    set conversionPagination(value: ConversionPagination) {
        // Store the value
        this._conversionPagination.next(value);
    }

    get conversionPagination$(): Observable<ConversionPagination> {
        return this._conversionPagination.asObservable();
    }

    /** Setter and Getter for Conversions Paginations */
    set discountPagination(value: DiscountPagination) {
        // Store the value
        this._discountPagination.next(value);
    }

    get discountPagination$(): Observable<DiscountPagination> {
        return this._discountPagination.asObservable();
    }

    /** Setter and Getter for Tree Products */
    set treeProducts(value: BranchObj[]) {
        this._treeProducts.next(value);
    }
    get treeProducts$(): Observable<BranchObj[]> {
        return this._treeProducts.asObservable();
    }

    /** Setter and Getter for Referral Services */
    set referralService(value: ReferralService[]) {
        this._referralService.next(value);
    }
    get referralService$(): Observable<ReferralService[]> {
        return this._referralService.asObservable();
    }

    /** Setter and Getter for Loyalty Services */
    set loyaltyService(value: ReferralService[]) {
        this._loyaltyService.next(value);
    }
    get loyaltyService$(): Observable<ReferralService[]> {
        return this._loyaltyService.asObservable();
    }

    /** Setter and Getter for Micro Services */
    set microService(value: ReferralService[]) {
        this._microService.next(value);
    }
    get microService$(): Observable<ReferralService[]> {
        return this._microService.asObservable();
    }

    /** Setter and Getter for Referral Products + Pagination */
    set referralProducts(value: ReferralProducts[]) {
        this._referralProducts.next(value);
    }
    get referralProducts$(): Observable<ReferralProducts[]> {
        return this._referralProducts.asObservable();
    }

    set referralProductPagination(value: ProductsPagination) {
        // Store the value
        this._referralProductsPagination.next(value);
    }

    get referralProductPagination$(): Observable<ProductsPagination> {
        return this._referralProductsPagination.asObservable();
    }

    /** Setter and Getter for Referral Users */
    set referralUsers(value: ReferralUsers[]) {
        this._referralUsers.next(value);
    }
    get referralUsers$(): Observable<ReferralUsers[]> {
        return this._referralUsers.asObservable();
    }

    /** Setter and Getter for Coins Referral User Paginations */
    set referralUsersPagination(value: Pagination) {
        // Store the value
        this._referralUsersPagination.next(value);
    }

    get referralUsersPagination$(): Observable<Pagination> {
        return this._referralUsersPagination.asObservable();
    }

    /** Setter and Getter for Coins History */
    set coinsHistory(value: CoinsHistory[]) {
        this._coinsHistory.next(value);
    }
    get coinsHistory$(): Observable<CoinsHistory[]> {
        return this._coinsHistory.asObservable();
    }

    /** Setter and Getter for Coins History Paginations */
    set coinsHistoryPagination(value: Pagination) {
        // Store the value
        this._coinsHistoryPagination.next(value);
    }

    get coinsHistoryPagination$(): Observable<Pagination> {
        return this._coinsHistoryPagination.asObservable();
    }

    /** Setter and Getter for Top Earner */
    set topEarner(value: Earner[]) {
        this._topEarner.next(value);
    }
    get topEarner$(): Observable<Earner[]> {
        return this._topEarner.asObservable();
    }

    /** Setter and Getter for Aging Data */
    set agingData(value: Aging[]) {
        this._agingData.next(value);
    }
    get agingData$(): Observable<Aging[]> {
        return this._agingData.asObservable();
    }

    /** Setter and Getter for Aging Data Paginations */
    set agingDataPagination(value: Pagination) {
        // Store the value
        this._agingDataPagination.next(value);
    }

    get agingDataPagination$(): Observable<Pagination> {
        return this._agingDataPagination.asObservable();
    }

    /** Setter and Getter for Members Listing */
    set membersList(value: Members[]) {
        this._membersList.next(value);
    }
    get membersList$(): Observable<Members[]> {
        return this._membersList.asObservable();
    }

    /** Setter and Getter for Members Listing Paginations */
    set membersListPagination(value: Pagination) {
        // Store the value
        this._membersListPagination.next(value);
    }

    get membersListPagination$(): Observable<Pagination> {
        return this._membersListPagination.asObservable();
    }

    /** Setter and Getter for Referral Tree */
    set referralTree(value: ReferralTree) {
        this._referralTree.next(value);
    }
    get referralTree$(): Observable<ReferralTree> {
        return this._referralTree.asObservable();
    }

    /** Setter and Getter for User Document */
    set userDocument(value: UserDoc[]) {
        this._userDoc.next(value);
    }
    get userDocument$(): Observable<UserDoc[]> {
        return this._userDoc.asObservable();
    }

    /** Setter and Getter for MicroDealer */
    set microDealers(value: MicrodealerDetails[]) {
        this._microDealer.next(value);
    }
    get microDealers$(): Observable<MicrodealerDetails[]> {
        return this._microDealer.asObservable();
    }

    /** Setter and Getter for Loyalty Config */
    set loyaltyConfig(value: LoyaltyConfig[]) {
        this._loyaltyConfig.next(value);
    }
    get loyaltyConfig$(): Observable<LoyaltyConfig[]> {
        return this._loyaltyConfig.asObservable();
    }

    /** Setter and Getter for Loyalty Config Paginations */
    set loyaltyConfigPagination(value: Pagination) {
        // Store the value
        this._loyaltyConfigPagination.next(value);
    }

    get loyaltyConfigPagination$(): Observable<Pagination> {
        return this._loyaltyConfigPagination.asObservable();
    }

    /** Setter and Getter for Referral Tree */
    set membershipInfo(value: MembershipInfo) {
        this._membershipInfo.next(value);
    }
    get membershipInfo$(): Observable<MembershipInfo> {
        return this._membershipInfo.asObservable();
    }

    /** Setter and Getter for Approval Data */
     set approvalData(value: Approval) {
        this._approvalData.next(value);
    }
    get approvalData$(): Observable<Approval> {
        return this._approvalData.asObservable();
    }

     /** Setter and Getter for Admins Data */
     set admins(value: Admins[]) {
        this._admins.next(value);
    }
    get admins$(): Observable<Admins[]> {
        return this._admins.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    getAllBill(
        params: {
            categoryId?: number;
            destinationId?: string;
            providerProductId?: number;
            destinationName?: string;
            page?: number;
            pageSize?: number;
            providerName?: string[];
            sendToMastercard?: string;
            sortBy?: string;
            sortingOrder?: string;
            status?: string[];
            parentCategoryId?: number;
            searchProduct?: string;
            productType?: string;
        } = {
                categoryId: null,
                destinationId: null,
                providerProductId: null,
                destinationName: null,
                page: null,
                pageSize: null,
                providerName: null,
                sendToMastercard: null,
                sortBy: null,
                sortingOrder: null,
                status: ['ACTIVE'],
                parentCategoryId: null,
                searchProduct: null,
                productType: null
            }
    ): Observable<any> {
        let orderService = this.getServiceEndpoint();
        let accessToken = this._authService.accessToken;

        const header = {
            headers: new HttpHeaders().set('Authorization', `${accessToken}`),
            params,
        };

        // Delete empty value
        (Object.keys(params) as (keyof typeof params)[]).forEach((key) => {
            const value = params[key];

            if (Array.isArray(value)) {
                (params as any)[key] = value.filter((element) => element !== null);
            }

            if (
                value === null ||
                value === undefined ||
                value === '' ||
                (Array.isArray(value) && value.length === 0)
            ) {
                delete (params as any)[key];
            }
        });

        return this._httpClient
            .get<any>(orderService + '/products/pagination', header)
            .pipe(
                tap((response) => {
                    const billsList = response.data.content;

                    this._logging.debug(
                        'Response from Bill Service (getAllBill)',
                        response
                    );

                    const billsPagination = {
                        length: response.data.totalElements,
                        size: response.data.size,
                        page: response.data.number,
                        lastPage: response.data.totalPages,
                        startIndex: response.data.pageable.offset,
                        endIndex:
                            response.data.pageable.offset +
                            response.data.numberOfElements -
                            1,
                    };

                    //  return bills pagination
                    this._billsPagination.next(billsPagination);

                    // return bills
                    this._bills.next(billsList);
                })
            );
    }

    postBill(body: Product, category: CategoryManagement): Observable<any> {
        let productService = this.getServiceEndpoint();
        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            ),
        };

        return this._httpClient
            .post<any>(productService + '/admins/products/create', body, header)
            .pipe(
                map((response) => {
                    this._logging.debug(
                        'Response from Bill Service (Create new product (postBill))',
                        response
                    );

                    const newBill = response.data;
                    if (newBill) {
                        newBill.productCategoryDetails = category;
                        
                        this.bills$.pipe(take(1)).subscribe(currentBills => {
                            this._bills.next([newBill, ...currentBills]);
                        });
                    }

                    return newBill;
                })
            );
    }

    editBill(id: string, body: Product): Observable<any> {
        let productService = this.getServiceEndpoint();
        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            ),
        };

        return this._httpClient
            .put<any>(productService + '/admins/products/edit/' + id, body, header)
            .pipe(
                map((response) => {
                    const updatedBill = response.data;

                    this.bills$.pipe(take(1)).subscribe(bills => {
                        let index = bills.findIndex((item) => item.id === updatedBill.id);
                        if (index > -1) {
                            bills[index] = updatedBill;
                            this._bills.next([...bills]);
                        }
                    });

                    return updatedBill;
                })
            );
    }

    deleteBill(id: number): Observable<any> {
        const deleteService = this.getServiceEndpoint();
        const accessToken = this._authService.accessToken;

        const headers = {
            headers: new HttpHeaders().set('Authorization', `Bearer ${accessToken}`),
        };

        return this.bills$.pipe(
            take(1),
            switchMap((bills) =>
                this._httpClient
                    .delete<any>(
                        `${deleteService}/admins/products/delete/${id}`,
                        headers
                    )
                    .pipe(
                        tap((response) => {
                            this._logging.debug(
                                'Response from Bill Service (deleteBill)',
                                response
                            );

                            const updatedBills = bills.filter(
                                (item) => item.id !== id
                            );

                            this._bills.next(updatedBills);
                        })
                    )
            )
        );
    }

    getIncompeleteCount(): Observable<any> {
        let billService = environment.production ? "https://api.e-kedai.my/api" : "https://uatapi.e-kedai.my/api";

        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            )
        };

        return this._httpClient
            .get<any>(billService + '/product/incomplete-count', header)
            .pipe(
                catchError((error) => {
                    // Handle the error, log it, and optionally rethrow it
                    console.error(
                        'Error from Bill Service (getIncompeleteCount)',
                        error
                    );
                    return throwError(error); // Propagate the error
                }),
                map((response) => {
                    this._logging.debug(
                        'Response from Bill Service (getIncompeleteCount)',
                        response
                    );
                    // Update the products
                    this._count.next(response.data);

                    return response.data;
                })
            );
    }

    getCategory(): Observable<any> {
        const url = `${this.getServiceEndpoint()}/products/all-category`;

        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            )
        };

        return this._httpClient.get<any>(url, header).pipe(
            tap((response) => {
                const list = response.data || [];

                this._categories.next(list);
                this._subCategories.next(list);
            })
        );
    }

    getCategoryById(id: number): Observable<ProductCategoryDetails> {
        const billService = this.getServiceEndpoint();

        const headers = new HttpHeaders().set(
            'Authorization',
            this._authService.accessToken
        );

        return this._httpClient
            .get<any>(`${billService}/products/category/${id}`, { headers })
            .pipe(
                map((response) => {
                    this._subCategory.next(response.data);
                    return response.data;
                }),
                catchError((error) => {
                    console.error('Error getCategoryById', error);
                    return throwError(() => error);
                })
            );
    }

    postImage(file: FormData, imageType: 'product' | 'banner' | 'profile' | 'ui' | 'document') {
        let billService = this.getServiceEndpoint();;
        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            ),
            params: { imageType }
        };

        return new Promise<ImageDetails>((resolve, reject) => {
            this._httpClient
                .post<any>(billService + '/assets/image-asset', file, header)
                .toPromise()
                .then((response: any) => {
                    this._logging.debug(
                        'Response from Bill Service (add new image (postImage))',
                        response
                    );
                    resolve(response.data);
                })
                .catch((error: any) => {
                    reject(error);
                });
        });
    }

    getCountry(): Observable<any> {
        let billService = this.getServiceEndpoint();;

        return this._httpClient.get<any>(billService + '/countries/all').pipe(
            catchError(() =>
                // Return false
                of(false)
            ),
            map((response) => {
                this._logging.debug(
                    'Response from Bill Service (getCountry)',
                    response
                );

                this._countries.next(response.data);

                return response.data;
            })
        );
    }

    getProductVariant(): Observable<any> {
        let billService = this.getServiceEndpoint();;

        return this._httpClient
            .get<any>(billService + '/products/type')
            .pipe(
                catchError(() =>
                    // Return false
                    of(false)
                ),
                map((response: string[]) => {
                    const newResponse = response.map(
                        (variant: string, index: number) => ({
                            id: index + 1,
                            variantType: variant,
                        })
                    );

                    this._logging.debug(
                        'Response from Bill Service (getProductVariant)',
                        newResponse
                    );

                    this._variantType.next(newResponse);

                    return newResponse;
                })
            );
    }

    postUpdateVariant(
        file: FormData,
        id: number,
        category: string
    ): Observable<any> {
        let ekedaiService = this.getServiceEndpoint();;
        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            ),
        };

        return this.bill$.pipe(
            take(1),
            switchMap((bill) =>
                this._httpClient.post(`${ekedaiService}/admins/products/variants/update/bulk/${id}/${category}`, file, header)
                    .pipe(
                        map((response: any) => {
                            this._logging.debug(
                                'Response from Bill Service (postUpdateVariant)',
                                response
                            );

                            const updatedBill = response.data;
                            bill = updatedBill;

                            // Update the products
                            this._bill.next(bill);

                            return response;
                        })
                    )
            )
        );
    }

    postUploadVariant(
        file: FormData,
        id: number,
        type: string
    ): Observable<any> {
        const ekedaiService = this.getServiceEndpoint();

        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            ),
        };

        return this.bill$.pipe(
            take(1),
            switchMap((bill) =>
                this._httpClient
                    .post(
                        `${ekedaiService}/admins/products/variants/upload/bulk/${id}/${type}`, file, header
                    )
                    .pipe(
                        map((response: any) => {
                            this._logging.debug(
                                'Response from Bill Service (editBillVariant)',
                                response
                            );

                            const updatedBill = response.data;
                            bill = updatedBill;

                            // Update the products
                            this._bill.next(bill);

                            return response;
                        })
                    )
            )
        );
    }

    editBillVariant(
        file: FormData,
        id: number,
        type: string
    ): Observable<any> {

        const productService = this.getServiceEndpoint();

        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            ),
            responseType: 'text' as 'json' 
        };

        return this.bills$.pipe(
            take(1),
            switchMap((bills) =>
                this._httpClient
                    .post<any>(
                        `${productService}/admins/products/variants/update/bulk/${id}/${type}`,
                        file,
                        header
                    )
                    .pipe(
                        map((response) => {
                            this._logging.debug(
                                'Response from Bill Service (editBillVariant)',
                                response
                            );

                            if (!response || typeof response === 'string' || !response.data) {
                                this._logging.warn('Response data is empty or custom format. Bypassing internal cache update.');
                                return { success: true, originalResponse: response }; 
                            }

                            const updatedBill = response.data;

                            if (updatedBill && updatedBill.id) {
                                const index = bills.findIndex(
                                    (item) => item.id === updatedBill.id
                                );

                                if (index > -1) {
                                    bills[index] = updatedBill;
                                }

                                this._bills.next(bills);
                            }

                            return updatedBill;
                        })
                    )
            )
        );
    }

    getBillById(id: number): Observable<any> {
        if (!id) {
            console.warn('id not provided');
            return of(true);
        }

        let billService = this.getServiceEndpoint();;

        return this._httpClient
            .get<any>(billService + '/products/' + id)
            .pipe(
                catchError(() =>
                    // Return false
                    of(false)
                ),
                map((response) => {
                    this._logging.debug(
                        'Response from Bill Service (get product by Id (getBillById))',
                        response
                    );
                    this._bill.next(response.data);
                    return response;
                })
            );
    }

    deleteBillVariant(productId: number, id: number): Observable<any> {
        let deleteService = this.getServiceEndpoint();;
        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            ),
        };

        return this.bills$.pipe(
            take(1),
            switchMap((bills) =>
                this._httpClient
                    .delete<any>(
                        deleteService + '/admins/products/variants/delete/' + id,
                        header
                    )
                    .pipe(
                        tap((response) => {
                            this._logging.debug(
                                'Response from Bill Service (deleteBillVariant by Id)',
                                response
                            );

                            const index = bills.findIndex(
                                (item) => item.id === productId
                            );

                            if (index > -1) {
                                const variantIndex = bills[
                                    index
                                ].productVariant.findIndex(
                                    (element) => element.id === id
                                );

                                if (variantIndex > -1) {
                                    bills[index].productVariant.splice(
                                        variantIndex,
                                        1
                                    );
                                    this._bills.next(bills);
                                }
                            }
                        })
                    )
            )
        );
    }

    getAllDiscount(
        params: { 
            calculationType?: string; 
            page?: number; 
            pageSize?: number; 
            sortBy?: string; 
            sortingOrder?: string; 
            status?: string; 
            globalSearch?: string; 
        } = { 
            calculationType: null, 
            page: 0, 
            pageSize: 20, 
            sortBy: 'createdDate', 
            sortingOrder: 'DESC', 
            status: null, 
            globalSearch: null 
        }
    ): Observable<any> {

        const billService = this.getServiceEndpoint();

        // clone supaya tak mutate input
        const cleanParams: any = { ...params };

        // clean params safely
        (Object.keys(cleanParams) as (keyof typeof params)[]).forEach((key) => {
            const value = cleanParams[key];

            if (Array.isArray(value)) {
                cleanParams[key] = value.filter((e) => e !== null);
            }

            if (
                value === null ||
                value === undefined ||
                value === '' ||
                (Array.isArray(value) && value.length === 0)
            ) {
                delete cleanParams[key];
            }
        });

        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            ),
            params: cleanParams,
        };

        return this._httpClient
            .get<any>(`${billService}/discount/get-discount`, header)
            .pipe(
                catchError((error) => throwError(() => error)),
                map((response) => {
                    this._logging.debug(
                        'Response from Bill Service (getAllDiscount)',
                        response
                    );

                    this._discounts.next(response.data.content);

                    this._discountPagination.next({
                        length: response.data.totalElements,
                        size: response.data.size,
                        page: response.data.number,
                        lastPage: response.data.totalPages,
                        startIndex: response.data.pageable.offset,
                        endIndex:
                            response.data.pageable.offset +
                            response.data.numberOfElements -
                            1,
                    });

                    return response.data;
                })
            );
    }

    getDiscountById(id: string): Observable<any> {
        let billService = this.getServiceEndpoint();;

        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            )
        };

        return this._httpClient
            .get<any>(billService + '/discount/get-discount-by-id/' + id, header)
            .pipe(
                catchError((error) => {
                    return throwError(error); // Propagate the error
                }),
                map((response) => {
                    this._logging.debug(
                        'Response from Bill Service (getDiscountById)',
                        response
                    );

                    return response.data;
                })
            );
    }

    // updateStatusDiscount(
    //     params: {
    //         discountCode?: string;
    //         deleteReason?: string;
    //         status?: string;
    //     } = {
    //             discountCode: null,
    //             deleteReason: null,
    //             status: null,
    //         }
    // ): Observable<Discount> {
    //     let discountService = this.getServiceEndpoint();;
    //     const header = {
    //         headers: new HttpHeaders().set(
    //             'Authorization',
    //             this._authService.accessToken
    //         ),
    //         params,
    //     };

    //     // Delete empty value
    //     Object.keys(params).forEach((key) => {
    //         if (Array.isArray(params[key])) {
    //             params[key] = params[key].filter((element) => element !== null);
    //         }
    //         if (
    //             params[key] === null ||
    //             params[key] === undefined ||
    //             params[key] === '' ||
    //             (Array.isArray(params[key]) && params[key].length === 0)
    //         ) {
    //             delete params[key];
    //         }
    //     });

    //     return this.discounts$.pipe(
    //         take(1),
    //         switchMap((discounts) =>
    //             this._httpClient
    //                 .put<any>(
    //                     discountService + '/discount/update-discount-status',
    //                     null,
    //                     header
    //                 )
    //                 .pipe(
    //                     map((response) => {
    //                         this._logging.debug(
    //                             'Response from Bill Service (updateStatusDiscount)',
    //                             response
    //                         );

    //                         const index = discounts.findIndex(
    //                             (item) =>
    //                                 item.discountCode === params.discountCode
    //                         );

    //                         if (response.status === 200) {
    //                             if (index) {
    //                                 discounts[index].status = params.status;
    //                                 discounts[index].deleteReason =
    //                                     params.deleteReason;
    //                             }
    //                             // Update the products
    //                             this._discounts.next(discounts);

    //                             return discounts[index];
    //                         }

    //                         return discounts[index];
    //                     })
    //                 )
    //         )
    //     );
    // }

    createDiscount(body: productDiscount): Observable<any> {
        let productService = this.getServiceEndpoint();;
        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            ),
        };

        return this.discounts$.pipe(
            take(1),
            switchMap((discount) =>
                this._httpClient
                    .post<any>(
                        productService + '/discount/create-discount',
                        body,
                        header
                    )
                    .pipe(
                        map((response) => {
                            this._logging.debug(
                                'Response from Bill Service (create Discount)',
                                response
                            );

                            const newDiscount = response.data.discountResult;

                            discount.unshift(newDiscount);

                            // Update the products
                            this._discounts.next(discount);

                            return newDiscount;
                        })
                    )
            )
        );
    }

    deleteVariant(body: DeleteVariant[]): Observable<any> {
        let productService = this.getServiceEndpoint();;
        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            ),
        };

        return this._httpClient
            .request<any>(
                'delete',
                `${productService}/discount/delete-discount-variant`,
                {
                    headers: header.headers,
                    body: body,
                }
            )
            .pipe(
                catchError((error) =>
                    // Return the error
                    throwError(error)
                ),
                tap((resp) => {
                    this._logging.debug(
                        'Response from Bill Service (deleteVariant)',
                        resp
                    );
                    return resp;
                })
            );
    }

    // updateDiscount(body: DiscountUpdate): Observable<Discount> {
    //     let discountService = this.getServiceEndpoint();;
    //     const header = {
    //         headers: new HttpHeaders().set(
    //             'Authorization',
    //             this._authService.accessToken
    //         ),
    //     };

    //     // Delete empty value
    //     Object.keys(body).forEach((key) => {
    //         if (Array.isArray(body[key])) {
    //             body[key] = body[key].filter((element) => element !== null);
    //         }
    //         if (
    //             body[key] === null ||
    //             body[key] === undefined ||
    //             body[key] === '' ||
    //             (Array.isArray(body[key]) && body[key].length === 0)
    //         ) {
    //             delete body[key];
    //         }
    //     });

    //     return this.discounts$.pipe(
    //         take(1),
    //         switchMap((discounts) =>
    //             this._httpClient
    //                 .put<any>(
    //                     discountService + '/discount/update-discount',
    //                     body,
    //                     header
    //                 )
    //                 .pipe(
    //                     map((response) => {
    //                         this._logging.debug(
    //                             'Response from Bill Service (updateDiscount)',
    //                             response
    //                         );

    //                         const index = discounts.findIndex(
    //                             (item) =>
    //                                 item.discountCode === body.discountCode
    //                         );

    //                         if (response.status === 200) {
    //                             if (index) {
    //                                 discounts[index].calculationType =
    //                                     body.calculationType;
    //                                 discounts[index].discountCode =
    //                                     body.discountCode;
    //                                 discounts[index].discountProductPage =
    //                                     body.discountProductPage;
    //                                 discounts[index].discountValue =
    //                                     body.discountValue;
    //                                 discounts[index].endDate = body.endDate;
    //                                 discounts[index].maxDiscountAmount =
    //                                     body.maxDiscountAmount;
    //                                 discounts[index].minimumSpend =
    //                                     body.minimumSpend;
    //                                 discounts[index].name = body.name;
    //                                 discounts[index].startDate = body.startDate;
    //                                 discounts[index].status = body.status;
    //                                 discounts[index].totalQuantity =
    //                                     body.totalQuantity;
    //                             }
    //                             // Update Discounts
    //                             this._discounts.next(discounts);

    //                             return discounts[index];
    //                         }

    //                         return discounts[index];
    //                     })
    //                 )
    //         )
    //     );
    // }

    getAllRewards(): Observable<Reward[]> {
        let RewardService = this._apiServer.settings.apiServer.loyaltyService;

        return this._httpClient
            .get<any>(RewardService + '/rewards-catalogue/get')
            .pipe(
                catchError((error) => {
                    // Handle the error, log it, and optionally rethrow it
                    console.error(
                        'Error from Bill Service (getAllRewards)',
                        error
                    );

                    return throwError(error); // Propagate the error
                }),
                map((response) => {
                    this._logging.debug(
                        'Response from Bill Service (getAllRewards)',
                        response
                    );

                    this._rewards.next(response.data);

                    return response.data;
                })
            );
    }

    createReward(body: Reward): Observable<any> {
        let RewardService = this._apiServer.settings.apiServer.loyaltyService;

        return this.rewards$.pipe(
            take(1),
            switchMap((rewards) =>
                this._httpClient
                    .post<any>(
                        RewardService + '/rewards-catalogue/create',
                        body
                    )
                    .pipe(
                        map((response) => {
                            this._logging.debug(
                                'Response from Bill Service (create Discount)',
                                response
                            );

                            const newBill = response.data;

                            rewards.unshift(newBill);

                            // Update the products
                            this._rewards.next(rewards);

                            return newBill;
                        })
                    )
            )
        );
    }

    // deleteVariantRewards(
    //     params: {
    //         id?: string;
    //         productCode?: string;
    //     } = {
    //             id: null,
    //             productCode: null,
    //         }
    // ): Observable<any> {
    //     let RewardService = this._apiServer.settings.apiServer.loyaltyService;

    //     const header = {
    //         params,
    //     };

    //     // Delete empty value
    //     Object.keys(params).forEach((key) => {
    //         if (Array.isArray(params[key])) {
    //             params[key] = params[key].filter((element) => element !== null);
    //         }
    //         if (
    //             params[key] === null ||
    //             params[key] === undefined ||
    //             params[key] === '' ||
    //             (Array.isArray(params[key]) && params[key].length === 0)
    //         ) {
    //             delete params[key];
    //         }
    //     });

    //     return this._httpClient
    //         .request<any>(
    //             'delete',
    //             `${RewardService}/rewards-catalogue/delete/variant/${params.id}/${params.productCode}`,
    //             header
    //         )
    //         .pipe(
    //             catchError((error) =>
    //                 // Return the error
    //                 throwError(error)
    //             ),
    //             tap((resp) => {
    //                 this._logging.debug(
    //                     'Response from Bill Service (deleteVariantRewards)',
    //                     resp
    //                 );
    //                 return resp;
    //             })
    //         );
    // }

    updateRewards(id: string, body: Reward): Observable<Reward> {
        let RewardService = this._apiServer.settings.apiServer.loyaltyService;

        // Delete empty value body
        (Object.keys(body) as (keyof Reward)[]).forEach((key) => {
            const value = body[key];

            if (Array.isArray(value)) {
                (body[key] as any) = value.filter((element) => element !== null);
            }

            if (
                value === null ||
                value === undefined ||
                value === '' ||
                (Array.isArray(value) && value.length === 0)
            ) {
                delete body[key];
            }
        });

        return this.rewards$.pipe(
            take(1),
            switchMap((rewards) =>
                this._httpClient
                    .put<any>(
                        RewardService + '/rewards-catalogue/update/' + id,
                        body
                    )
                    .pipe(
                        map((response) => {
                            this._logging.debug(
                                'Response from Bill Service (updateRewards)',
                                response
                            );

                            const index = rewards.findIndex(
                                (item) => item._id === body._id
                            );
                            // debugger;
                            if (response.status === 200) {
                                if (index) {
                                    rewards[index].rewardName = body.rewardName;
                                    rewards[index].description =
                                        body.description;
                                    rewards[index].category = body.category;
                                    rewards[index].status = body.status;
                                    rewards[index].variants = body.variants;
                                    rewards[index].wspUrl = body.wspUrl;
                                }
                                // Update Rewards
                                this._rewards.next(rewards);

                                return rewards[index];
                            }

                            return rewards[index];
                        })
                    )
            )
        );
    }

    postRewardImage(id: string, file: File) {
        let RewardService = this._apiServer.settings.apiServer.loyaltyService;

        // Create form data
        const formData = new FormData();
        formData.append('file', file);

        return new Promise<ImageDetails>((resolve, reject) => {
            this._httpClient
                .post<any>(
                    `${RewardService}/rewards-catalogue/${id}/upload-image`,
                    formData
                )
                .toPromise()
                .then((response: any) => {
                    this._logging.debug(
                        'Response from Bill Service (postRewardImage)',
                        response
                    );
                    resolve(response.data);
                })
                .catch((error: any) => {
                    reject(error);
                });
        });
    }

    getAllConversions(
        params: {
            page?: number;
            pageSize?: number;
            searchKey?: string;
            sortBy?: string;
            sortOrder?: string;
        } = {
            page: null,
            pageSize: null,
            searchKey: null,
            sortBy: null,
            sortOrder: null,
        }
    ): Observable<any> {

        const RewardService = this._apiServer.settings.apiServer.loyaltyService;

        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) =>
                value !== null &&
                value !== undefined &&
                value !== ''
            )
        );

        const header = {
            params: cleanParams
        };

        return this._httpClient
            .get<any>(
                `${RewardService}/conversion-setup/get-conversions`,
                header
            )
            .pipe(
                tap((response) => {

                    this._logging.debug(
                        'Response from Bill Service (getAllConversions)',
                        response
                    );

                    const billsList = response.data.data;

                    const pagination = {
                        length: response.data.pagination.length,
                        size: response.data.pagination.size,
                        page: response.data.pagination.page,
                        lastPage: response.data.pagination.lastPage,
                        startIndex: response.data.pagination.startIndex,
                        endIndex: response.data.pagination.endIndex,
                    };

                    this._conversionPagination.next(pagination);
                    this._conversion.next(billsList);
                })
            );
    }

    updateConversions(id: string, body: Conversion): Observable<Conversion> {
        let RewardService = this._apiServer.settings.apiServer.loyaltyService;

        if (body.serviceConversions.length > 0) {
            (Object.keys(body) as (keyof Conversion)[]).forEach((key) => {
                if (body.serviceConversions?.length > 0) {

                    const keys = Object.keys(body) as (keyof Conversion)[];

                    keys.forEach((key) => {
                        const value = body[key];

                        if (Array.isArray(value)) {
                            (body[key] as any) = value.filter((e) => e !== null);
                        }

                        if (
                            value === null ||
                            value === undefined ||
                            value === '' ||
                            (Array.isArray(value) && value.length === 0)
                        ) {
                            delete body[key];
                        }
                    });
                }
            });
        }

        return this.conversion$.pipe(
            take(1),
            switchMap((conversion) =>
                this._httpClient
                    .put<any>(
                        RewardService + '/conversion-setup/update/' + id,
                        body
                    )
                    .pipe(
                        map((response) => {
                            this._logging.debug(
                                'Response from Bill Service (updateConversions)',
                                response
                            );

                            const index = conversion.findIndex(
                                (item) => item._id === body._id
                            );
                            // debugger;
                            if (response.status === 200) {
                                if (index) {
                                    conversion[index].channel = body.channel;
                                    conversion[index].expiryDuration =
                                        body.expiryDuration;
                                    conversion[index].serviceConversions =
                                        body.serviceConversions;
                                }
                                // Update Conversions
                                this._conversion.next(conversion);

                                return conversion[index];
                            }

                            return conversion[index];
                        })
                    )
            )
        );
    }

    createConversion(body: Conversion): Observable<any> {

        const RewardService = this._apiServer.settings.apiServer.loyaltyService;

        const cleanBody = Object.fromEntries(
            Object.entries(body).filter(([_, value]) =>
                value !== null &&
                value !== undefined &&
                value !== '' &&
                !(Array.isArray(value) && value.length === 0)
            )
        );

        // Log request body
        this._logging.debug('Request body before service call', cleanBody);

        return this.conversion$.pipe(
            take(1),
            switchMap((conversion) =>
                this._httpClient
                    .post<any>(
                        `${RewardService}/conversion-setup/store`,
                        cleanBody
                    )
                    .pipe(
                        catchError((error) => {
                            this._logging.error(
                                'Error creating conversion:',
                                error
                            );
                            return throwError(() => error);
                        }),
                        map((response) => {

                            this._logging.debug(
                                'Response from Loyalty Service (createConversion)',
                                response
                            );

                            const newConversion = response.data;

                            conversion.unshift(newConversion);

                            this._conversion.next(conversion);

                            return newConversion;
                        })
                    )
            )
        );
    }

    getCategoriesTree(
        params: { level?: string } = { level: null }
    ): Observable<any[]> {

        const ekedaiService = this.getServiceEndpoint();

        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) =>
                value !== null &&
                value !== undefined &&
                value !== ''
            )
        );

        const header = {
            params: cleanParams
        };

        return this._httpClient
            .get<any>(`${ekedaiService}/products/category/products-variants`, header)
            .pipe(
                catchError((error) => {
                    console.error(
                        'Error from Bill Service (getCategoriesTree)',
                        error
                    );
                    return throwError(() => error);
                }),
                map((response) => {

                    this._logging.debug(
                        'Response from Bill Service (getCategoriesTree)',
                        response
                    );

                    this._treeProducts.next(response.data);

                    return response.data;
                })
            );
    }

    getReferralServices(
        params: { channel?: string } = { channel: null }
    ): Observable<any[]> {

        const loyaltyService = this._apiServer.settings.apiServer.loyaltyService;

        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) =>
                value !== null &&
                value !== undefined &&
                value !== ''
            )
        );

        const header = {
            params: cleanParams
        };

        return this._httpClient
            .get<any>(
                `${loyaltyService}/referral/get-service-list`,
                header
            )
            .pipe(
                catchError((error) => {
                    console.error(
                        'Error from Bill Service (getReferralServices)',
                        error
                    );
                    return throwError(() => error);
                }),
                map((response) => {

                    this._logging.debug(
                        'Response from Bill Service (getReferralServices)',
                        response
                    );

                    this._referralService.next(response.data);

                    return response.data;
                })
            );
    }

    getLoyaltyServices(
        params: { channel?: string } = { channel: null }
    ): Observable<any[]> {

        const loyaltyService = this._apiServer.settings.apiServer.loyaltyService;

        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) =>
                value !== null &&
                value !== undefined &&
                value !== ''
            )
        );

        const header = {
            params: cleanParams
        };

        return this._httpClient
            .get<any>(
                `${loyaltyService}/loyalty/get-service-list`,
                header
            )
            .pipe(
                catchError((error) => {
                    console.error(
                        'Error from Bill Service (getLoyaltyServices)',
                        error
                    );

                    return throwError(() => error);
                }),
                map((response) => {

                    this._logging.debug(
                        'Response from Bill Service (getLoyaltyServices)',
                        response
                    );

                    this._loyaltyService.next(response.data);

                    return response.data;
                })
            );
    }

    getMicroServices(
        params: { channel?: string } = { channel: null }
    ): Observable<any[]> {

        const loyaltyService = this._apiServer.settings.apiServer.loyaltyService;

        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) =>
                value !== null &&
                value !== undefined &&
                value !== ''
            )
        );

        const header = {
            params: cleanParams
        };

        return this._httpClient
            .get<any>(
                `${loyaltyService}/micro-dealer/get-service-list`,
                header
            )
            .pipe(
                catchError((error) => {
                    console.error(
                        'Error from Bill Service (getMicroServices)',
                        error
                    );

                    return throwError(() => error);
                }),
                map((response) => {

                    this._logging.debug(
                        'Response from Bill Service (getMicroServices)',
                        response
                    );

                    this._microService.next(response.data);

                    return response.data;
                })
            );
    }

    getAllReferralProduct(): Observable<any> {
        let ekedaiService = this.getServiceEndpoint();;

        return this._httpClient
            .get<any>(
                ekedaiService + '/product/service-ids-with-products'
            )
            .pipe(
                tap((response) => {
                    const productList = response.data;

                    this._logging.debug(
                        'Response from Bill Service (getAllReferralProducts)',
                        response
                    );

                    // return bills
                    this._referralProducts.next(productList);

                    return productList;
                })
            );
    }

    updateReferralProduct(body: any[]): Observable<any> {
        let ekedaiService = this.getServiceEndpoint();;
        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            ),
        };

        return this._httpClient
            .put<any>(
                ekedaiService + '/product/update-service-id/batch',
                body,
                header
            )
            .pipe(
                catchError((error) => {
                    // Handle the error, log it, and optionally rethrow it
                    console.error(
                        'Error from Bill Service (updateReferralProduct)',
                        error
                    );

                    return throwError(error); // Propagate the error
                }),
                map((response) => {
                    this._logging.debug(
                        'Response from Bill Service (updateReferralProduct)',
                        response
                    );

                    const updatedProducts = response.data;

                    return updatedProducts;
                })
            );
    }

    getReferralUsers(
        params: {
            channel?: string;
            page?: number;
            pageSize?: number;
        } = {
            channel: 'ALL',
            page: null,
            pageSize: null,
        }
    ): Observable<any> {

        const profileService = this._apiServer.settings.apiServer.profileService;

        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) =>
                value !== null &&
                value !== undefined &&
                value !== ''
            )
        );

        const header = {
            params: cleanParams,
        };

        return this._httpClient
            .get<any>(
                `${profileService}/admin-referral/get-referral-users`,
                header
            )
            .pipe(
                tap((response) => {

                    const userList = response?.context?.records ?? [];

                    this._logging.debug(
                        'Response from Bill Service (getReferralUsers)',
                        response
                    );

                    this._referralUsersPagination.next({
                        length: response.context.pagination.length,
                        size: response.context.pagination.size,
                        page: response.context.pagination.page,
                        lastPage: response.context.pagination.lastPage,
                        startIndex: response.context.pagination.startIndex,
                        endIndex: response.context.pagination.endIndex,
                    });

                    this._referralUsers.next(userList);

                    return userList;
                })
            );
    }

    setReferralUser(phone: string, refCode: string, params: { channel: string } = { channel: null }): Observable<any> {
        let profileService = this._apiServer.settings.apiServer.profileService;

        const header = {
            params,
        };

        return this._httpClient.post<any>(profileService + '/admin-referral/add/' + phone + '/' + refCode, null, header).pipe(
            catchError((error) => {
                this._logging.error(
                    'Error setting referral code to user:',
                    error
                );
                return throwError(error);
            }),
            map((response) => {
                this._logging.debug(
                    'Response from Bill Service (setReferralUser)',
                    response
                );

                return response;
            })
        );
    }

    removeAgentUser(phone: string, params: { channel: string } = { channel: null }): Observable<Conversion> {
        let profileService = this._apiServer.settings.apiServer.profileService;

        const header = {
            params
        };

        return this._httpClient.put<any>(profileService + '/admin-referral/remove-referral-code/' + phone, null, header).pipe(
            catchError((error) => {
                this._logging.error(
                    'Error remove referral code from agent user:',
                    error
                );
                return throwError(error);
            }),
            map((response) => {
                this._logging.debug(
                    'Response from Bill Service (removeAgentUser)',
                    response
                );

                return response;
            })
        );
    }

    getAllCoinsHistory(
        params: {
            page?: number;
            pageSize?: number;
            search?: string;
            sortBy?: string;
            type?: string;
            channel?: string;
            startDate?: string;
            endDate?: string;
        } = {
            page: 1,
            pageSize: 20,
            search: null,
            sortBy: 'DESC-EARNED',
            type: 'ALL',
            channel: 'ALL',
            startDate: null,
            endDate: null,
        }
    ): Observable<any> {

        const loyaltyService = this._apiServer.settings.apiServer.loyaltyService;

        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) =>
                value !== null &&
                value !== undefined &&
                value !== ''
            )
        );

        const header = {
            params: cleanParams
        };

        return this._httpClient
            .get<any>(
                `${loyaltyService}/admin/api/get-coins-history`,
                header
            )
            .pipe(
                map((response) => {

                    const coinsHistoryList = response.data.records;

                    this._logging.debug(
                        'Response from Bill Service (getAllCoinsHistory)',
                        response
                    );

                    this._coinsHistoryPagination.next({
                        length: response.data.pagination.length,
                        size: response.data.pagination.size,
                        page: response.data.pagination.page,
                        lastPage: response.data.pagination.lastPage,
                        startIndex: response.data.pagination.startIndex,
                        endIndex: response.data.pagination.endIndex,
                    });

                    this._coinsHistory.next(coinsHistoryList);

                    return coinsHistoryList;
                })
            );
    }

    getTopEarner(
        params: {
            type?: string;
            channel?: string;
        } = {
            type: 'ALL',
            channel: null
        }
    ): Observable<any> {

        const loyaltyService = this._apiServer.settings.apiServer.loyaltyService;

        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) =>
                value !== null &&
                value !== undefined &&
                value !== ''
            )
        );

        const header = {
            params: cleanParams
        };

        return this._httpClient
            .get<any>(
                `${loyaltyService}/admin/api/get-top-earner`,
                header
            )
            .pipe(
                map((response) => {

                    this._logging.debug(
                        'Response from Bill Service (getTopEarner)',
                        response
                    );

                    const topEarnerList = response.data;

                    this._topEarner.next(topEarnerList);

                    return topEarnerList;
                })
            );
    }

    getAgingData(
        params: {
            page?: number;
            pageSize?: number;
            month?: number;
            year?: number;
            channel?: string;
        } = {
            page: 1,
            pageSize: 20,
            month: null,
            year: null,
            channel: null,
        }
    ): Observable<any> {

        const loyaltyService = this._apiServer.settings.apiServer.loyaltyService;

        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) =>
                value !== null &&
                value !== undefined &&
                value !== ''
            )
        );

        const header = {
            params: cleanParams,
        };

        return this._httpClient
            .get<any>(
                `${loyaltyService}/admin/api/get-aging-data`,
                header
            )
            .pipe(
                map((response) => {

                    this._logging.debug(
                        'Response from Bill Service (getAgingData)',
                        response
                    );

                    const agingDataList = response.data.records;

                    this._agingDataPagination.next({
                        length: response.data.pagination.length,
                        size: response.data.pagination.size,
                        page: response.data.pagination.page,
                        lastPage: response.data.pagination.lastPage,
                        startIndex: response.data.pagination.startIndex,
                        endIndex: response.data.pagination.endIndex,
                    });

                    this._agingData.next(agingDataList);

                    return agingDataList;
                })
            );
    }

    getAllMembers(
        params: {
            page?: number;
            pageSize?: number;
            search?: string | null;
            sortBy?: string;
            channel?: string;
            status?: string;
            type?: string | null;
        } = {
            page: 1,
            pageSize: 20,
            search: null,
            sortBy: 'A-Z',
            channel: 'ALL',
            status: 'ALL',
            type: null,
        }
    ): Observable<any> {

        const loyaltyService = this._apiServer.settings.apiServer.loyaltyService;

        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) =>
                value !== null &&
                value !== undefined &&
                value !== ''
            )
        );

        const header = {
            params: cleanParams
        };

        return this._httpClient
            .get<any>(
                `${loyaltyService}/admin/api/get-members-list`,
                header
            )
            .pipe(
                map((response) => {

                    const membersList = response.data.records;

                    this._logging.debug(
                        'Response from Bill Service (getAllMembers)',
                        response
                    );

                    this._membersListPagination.next({
                        length: response.data.pagination.length,
                        size: response.data.pagination.size,
                        page: response.data.pagination.page,
                        lastPage: response.data.pagination.lastPage,
                        startIndex: response.data.pagination.startIndex,
                        endIndex: response.data.pagination.endIndex,
                    });

                    this._membersList.next(membersList);

                    return membersList;
                })
            );
    }

    getReferralTree(
        params: {
            phone?: string;
            channel?: string;
        } = {
            phone: null,
            channel: null,
        }
    ): Observable<any> {

        const loyaltyService = this._apiServer.settings.apiServer.loyaltyService;

        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) =>
                value !== null &&
                value !== undefined &&
                value !== ''
            )
        );

        const header = {
            params: cleanParams,
        };

        return this._httpClient
            .get<any>(
                `${loyaltyService}/admin/api/get-referral-tree`,
                header
            )
            .pipe(
                map((response) => {

                    this._logging.debug(
                        'Response from Bill Service (getReferralTree)',
                        response
                    );

                    this._referralTree.next(response.data);

                    return response.data;
                })
            );
    }

    getUserDocument(userId: string): Observable<any> {
        let ekedaiService = this.getServiceEndpoint();;
        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            )
        };

        return this._httpClient
            .get<any>(ekedaiService + '/user-documents/user/' + userId, header)
            .pipe(
                catchError((error) => {
                    console.error('An error occurred:', error);
                    return throwError(error); // Propagate the error
                }),
                map((response) => {
                    this._logging.debug(
                        'Response from User Service (getUserDocument)',
                        response
                    );

                    this._userDoc.next(response.data);

                    return response;
                })
            );
    }

    updateDocumentStatus(id: string, status: 'PENDING_UPLOAD' | 'VERIFIED' | 'REJECTED' | 'PENDING_VERIFICATION'): Observable<any> {
        let ekedaiService = this.getServiceEndpoint();;
        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            ),
            params: { status: status }
        };

        return this.userDocument$.pipe(
            take(1),
            switchMap((userDocument) =>
                this._httpClient
                    .put<any>(ekedaiService + '/user-documents/update-status/' + id, null, header)
                    .pipe(
                        map((response) => {
                            this._logging.debug('Response from User Service (updateDocumentStatus)', response);

                            let index = userDocument.findIndex(user => user.id === response.data.id);
                            if (index > -1) {
                                userDocument[index] = response.data;
                            }

                            this._userDoc.next(userDocument);

                            return response.data;
                        })
                    )
            )
        );
    }

    getMicroDealer(
        params: {
            page?: number;
            pageSize?: number;
            channel?: string;
        } = {
            page: 1,
            pageSize: 100,
            channel: 'ALL',
        }
    ): Observable<any> {

        const loyaltyService = this._apiServer.settings.apiServer.loyaltyService;

        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) =>
                value !== null &&
                value !== undefined &&
                value !== ''
            )
        );

        const header = {
            params: cleanParams
        };

        return this._httpClient
            .get<any>(
                `${loyaltyService}/micro-dealer/get-users-list`,
                header
            )
            .pipe(
                map((response) => {

                    const microDealerList = response.data.records;

                    this._logging.debug(
                        'Response from Bill Service (getMicroDealer)',
                        response
                    );

                    this._microDealer.next(microDealerList);

                    return microDealerList;
                })
            );
    }

    setMicrodealer(
        params: { phone?: string; channel?: string } = {
            phone: null,
            channel: 'ALL'
        }
    ): Observable<any> {

        const loyaltyService = this._apiServer.settings.apiServer.loyaltyService;

        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) =>
                value !== null &&
                value !== undefined &&
                value !== ''
            )
        );

        const header = {
            params: cleanParams
        };

        return this._httpClient
            .post<any>(
                `${loyaltyService}/micro-dealer/setup-dealers`,
                null,
                header
            )
            .pipe(
                catchError((error) => {
                    this._logging.error(
                        'Error add new Microdealer:',
                        error
                    );
                    return throwError(() => error);
                }),
                map((response) => {
                    this._logging.debug(
                        'Response from Bill Service (setMicrodealer)',
                        response
                    );

                    return response;
                })
            );
    }

    updateDealerStatus(
        params: {
            phone?: string;
            channel?: string;
            status?: string;
        } = {
            phone: null,
            channel: null,
            status: null,
        }
    ): Observable<MicroDealer> {

        const loyaltyService = this._apiServer.settings.apiServer.loyaltyService;

        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) =>
                value !== null &&
                value !== undefined &&
                value !== ''
            )
        ) as {
            phone?: string;
            channel?: string;
            status?: string;
        };

        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            ),
            params: cleanParams,
        };

        return this.microDealers$.pipe(
            take(1),
            switchMap((microDealers) =>
                this._httpClient
                    .put<any>(
                        `${loyaltyService}/micro-dealer/update-status/${cleanParams.phone}`,
                        null,
                        header
                    )
                    .pipe(
                        map((response) => {

                            this._logging.debug(
                                'Response from Bill Service (updateDealerStatus)',
                                response
                            );

                            const dealerIndex = microDealers.findIndex(
                                (dealer) => dealer.phone === cleanParams.phone
                            );

                            if (dealerIndex !== -1 && response.status === 200) {

                                const channelIndex =
                                    microDealers[dealerIndex].microDealer.findIndex(
                                        (item) =>
                                            item.channel === cleanParams.channel
                                    );

                                if (channelIndex !== -1) {
                                    microDealers[dealerIndex]
                                        .microDealer[channelIndex]
                                        .status = cleanParams.status;
                                }

                                this._microDealer.next(microDealers);

                                return microDealers[dealerIndex]
                                    .microDealer[channelIndex];
                            }

                            return null;
                        })
                    )
            )
        );
    }

    getLoyaltyConfig(
        params: {
            page?: number | null;
            pageSize?: number | null;
        } = {
            page: null,
            pageSize: null
        }
    ): Observable<any> {

        const loyaltyService = this._apiServer.settings.apiServer.loyaltyService;

        const cleanParams = Object.fromEntries(
            (Object.entries(params) as [string, string | number | null | undefined][])
                .filter(([_, value]) => {
                    if (value === null || value === undefined) return false;
                    if (typeof value === 'string' && value.trim() === '') return false;
                    return true;
                })
        );

        const header = {
            params: cleanParams
        };

        return this._httpClient
            .get<any>(
                `${loyaltyService}/loyalty-config/get-list`,
                header
            )
            .pipe(
                map((response) => {

                    this._logging.debug(
                        'Response from Bill Service (getLoyaltyConfig)',
                        response
                    );

                    const loyaltyConfig = response.data.records;

                    this._loyaltyConfig.next(loyaltyConfig);

                    this._loyaltyConfigPagination.next({
                        length: response.data.pagination.length,
                        size: response.data.pagination.size,
                        page: response.data.pagination.page,
                        lastPage: response.data.pagination.lastPage,
                        startIndex: response.data.pagination.startIndex,
                        endIndex: response.data.pagination.endIndex,
                    });

                    return loyaltyConfig;
                })
            );
    }

    setLoyaltyConfig(body: any): Observable<any> {
        let loyaltyService = this._apiServer.settings.apiServer.loyaltyService;

        return this._httpClient.post<any>(loyaltyService + '/loyalty-config/store', body).pipe(
            catchError((error) => {
                this._logging.error(
                    'Error setting setting up loyalty config:',
                    error
                );
                return throwError(error);
            }),
            map((response) => {
                this._logging.debug(
                    'Response from Bill Service (setLoyaltyConfig)',
                    response
                );

                return response;
            })
        );
    }

    editLoyaltyConfig(id: string, body: any): Observable<any> {
        let loyaltyService = this._apiServer.settings.apiServer.loyaltyService;

        return this._httpClient.put<any>(loyaltyService + '/loyalty-config/update/' + id, body).pipe(
            catchError((error) => {
                this._logging.error(
                    'Error updating loyalty config',
                    error
                );
                return throwError(error);
            }),
            map((response) => {
                this._logging.debug(
                    'Response from Bill Service (editLoyaltyConfig)',
                    response
                );

                return response;
            })
        );
    }

    getMembershipInfo(
        params: {
            phone?: string;
            channel?: string;
        } = {
            phone: null,
            channel: 'e-kedai'
        }
    ): Observable<any> {

        const loyaltyService = this._apiServer.settings.apiServer.loyaltyService;

        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) =>
                value !== null &&
                value !== undefined &&
                value !== ''
            )
        );

        const header = {
            params: cleanParams,
        };

        return this._httpClient
            .get<any>(
                `${loyaltyService}/admin/api/get-membership-info`,
                header
            )
            .pipe(
                map((response) => {

                    const membershipInfo = response.data;

                    this._logging.debug(
                        'Response from Bill Service (getMembershipInfo)',
                        response
                    );

                    this._membershipInfo.next(membershipInfo);

                    return membershipInfo;
                })
            );
    }

    editMemberTier(
        params: {
            phone?: string | null;
            channel?: string | null;
            tier?: number | null;
            status?: string | null;
        } = {
            phone: null,
            channel: 'e-kedai',
            tier: null,
            status: null
        }
    ): Observable<any> {
        let loyaltyService = this._apiServer.settings.apiServer.loyaltyService;

        (Object.keys(params) as (keyof typeof params)[]).forEach((key) => {
            const value = params[key];

            if (Array.isArray(value)) {
                const filtered = value.filter((e) => e !== null);

                if (filtered.length > 0) {
                    (params[key] as any) = filtered;
                } else {
                    delete params[key];
                }
                return;
            }

            if (value === null || value === undefined || value === '') {
                delete params[key];
            }
        });

        return this._httpClient
            .put<any>(
                loyaltyService + '/admin/api/update-membership-tier',
                null,
                { params }
            )
            .pipe(
                map((response) => {
                    this._logging.debug(
                        'Response from Bill Service (editMemberTier)',
                        response
                    );
                    return response;
                })
            );
    }

    getApprovalData(
        params: {
            page?: number;
            pageSize?: number;
            type?: string | null;
            status?: string | null;
        } = {
            page: 1,
            pageSize: 20,
            type: null,
            status: null
        }
    ): Observable<any> {
        let loyaltyService = this._apiServer.settings.apiServer.loyaltyService;

        (Object.keys(params) as (keyof typeof params)[]).forEach((key) => {
            const value = params[key];

            if (value === null || value === undefined || value === '') {
                delete params[key];
            }
        });

        return this._httpClient
            .get<any>(
                loyaltyService + '/authorization/get-by-type',
                { params }
            )
            .pipe(
                map((response) => {
                    const approvalData = response.data;

                    this._logging.debug(
                        'Response from Bill Service (getApprovalData)',
                        response
                    );

                    this._approvalData.next(approvalData);

                    return approvalData;
                })
            );
    }

    getAdminInfo(): Observable<any> {
        let loyaltyService = this._apiServer.settings.apiServer.loyaltyService;

        return this._httpClient
            .get<any>(
                loyaltyService + '/admin/api/admins'
            )
            .pipe(
                map((response) => {
                    const adminInfo = response.data;

                    this._logging.debug(
                        'Response from Bill Service (getAdminInfo)',
                        response
                    );

                    // return data
                    this._admins.next(adminInfo);

                    return adminInfo;
                })
            );
    }

    approveConfig(
        params: {
            type?: string | null;
            originId?: string | null;
            id?: string | null;
            approverId?: string | null;
            status?: string | null;
        } = {
            type: null,
            originId: null,
            id: null,
            approverId: null,
            status: null
        }
    ): Observable<any> {
        let loyaltyService = this._apiServer.settings.apiServer.loyaltyService;

        (Object.keys(params) as (keyof typeof params)[]).forEach((key) => {
            const value = params[key];

            if (Array.isArray(value)) {
                const filtered = value.filter((element) => element !== null);

                if (filtered.length > 0) {
                    (params[key] as any) = filtered;
                } else {
                    delete params[key];
                }
                return;
            }

            if (value === null || value === undefined || value === '') {
                delete params[key];
            }
        });

        return this._httpClient
            .post<any>(
                loyaltyService + '/authorization/approval',
                null,
                { params }
            )
            .pipe(
                catchError((error) => {
                    this._logging.error(
                        'Error approve or rejecting config:',
                        error
                    );
                    return throwError(error);
                }),
                map((response) => {
                    this._logging.debug(
                        'Response from Bill Service (approveConfig)',
                        response
                    );
                    return response;
                })
            );
    }
}
